import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

export const DEFAULT_ORDERING_MESSAGE =
  'Online ordering is temporarily unavailable. You can still check pricing and availability.';

const DEFAULT_SETTINGS = { enabled: true, message: DEFAULT_ORDERING_MESSAGE };

const normalise = (value) => ({
  enabled: value?.enabled !== false,
  message: value?.message || DEFAULT_ORDERING_MESSAGE
});

const OrderingContext = createContext({
  ...DEFAULT_SETTINGS,
  loading: false,
  refresh: () => {}
});

/**
 * Fetch the global ordering setting directly from the database.
 * Use this for a fresh check immediately before submitting an order.
 * Fails open (enabled) - the orders table trigger is the real enforcement.
 * @returns {Promise<{enabled: boolean, message: string}>}
 */
export const fetchOrderingSettings = async () => {
  try {
    const { data, error } = await supabase
      .from('system_settings')
      .select('setting_value')
      .eq('setting_key', 'ordering_enabled')
      .maybeSingle();

    if (error) {
      console.warn('Could not fetch ordering setting:', error);
      return DEFAULT_SETTINGS;
    }
    return normalise(data?.setting_value);
  } catch (err) {
    console.error('Error fetching ordering setting:', err);
    return DEFAULT_SETTINGS;
  }
};

/**
 * Provides the global "online ordering" setting to the app with a single
 * realtime subscription, so every consumer updates live when an admin toggles it.
 */
export const OrderingProvider = ({ session, children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await fetchOrderingSettings();
    setSettings(next);
    setLoading(false);
  }, []);

  useEffect(() => {
    // system_settings is only readable by authenticated users
    if (!session) {
      setSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return undefined;
    }

    refresh();

    const channel = supabase
      .channel('ordering_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings',
          filter: 'setting_key=eq.ordering_enabled'
        },
        (payload) => {
          if (payload.new && payload.new.setting_value) {
            setSettings(normalise(payload.new.setting_value));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [session, refresh]);

  return (
    <OrderingContext.Provider value={{ ...settings, loading, refresh }}>
      {children}
    </OrderingContext.Provider>
  );
};

/**
 * @returns {{ enabled: boolean, message: string, loading: boolean, refresh: Function }}
 */
export const useOrderingEnabled = () => useContext(OrderingContext);

export default useOrderingEnabled;
